import { NavLink } from 'react-router-dom';
import { cn } from '@/lib/cn';
import { Icon } from '@/components/icons/Icon';
import type { NavItem } from './navItems';

/**
 * SideRail — the tablet and desktop navigation.
 *
 * §11: tablet is 768–1279 with "rail + master–detail". Moving the five tabs to
 * a persistent left rail frees the bottom edge, which on a tablet is nowhere
 * near a thumb anyway, and gives the content column its full height back.
 *
 * The rail is deep blue rather than white so the eye reads it as chrome and
 * stops treating it as content — the same reason the seller and admin consoles
 * use it.
 */

export interface SideRailProps {
  items: NavItem[];
  /** Compact icon rail (tablet) vs. labelled rail (desktop console). */
  variant?: 'compact' | 'labelled';
  className?: string;
}

export function SideRail({ items, variant = 'compact', className }: SideRailProps) {
  if (variant === 'labelled') {
    return (
      <nav
        data-testid="side-rail"
        aria-label="Primary"
        className={cn('flex w-[236px] shrink-0 flex-col gap-[3px] bg-blue-deep p-3 pt-[18px]', className)}
      >
        {items.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={({ isActive }) =>
              cn(
                'flex items-center gap-2.5 rounded-[9px] px-3 py-2.5 text-[13px] font-medium no-underline',
                isActive ? 'bg-white/[0.13] font-bold text-white' : 'text-[#B9CBEC] hover:text-white',
              )
            }
          >
            <Icon name={item.icon} size={17} strokeWidth={1.8} />
            {item.label}
            {typeof item.badge === 'number' ? (
              <span className="ml-auto rounded-pill bg-danger px-[7px] py-px text-[10px] font-bold text-white">
                {item.badge}
              </span>
            ) : null}
          </NavLink>
        ))}
      </nav>
    );
  }

  return (
    <nav
      data-testid="side-rail"
      aria-label="Primary"
      className={cn(
        'hidden w-[78px] shrink-0 flex-col items-center gap-1 bg-blue-deep px-2 py-4 md:flex',
        className,
      )}
    >
      {items.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          className={({ isActive }) =>
            cn(
              'relative w-full rounded-[11px] py-2.5 text-center',
              isActive ? 'bg-white/[0.13] text-white' : 'text-[#B9CBEC] hover:text-white',
            )
          }
        >
          {({ isActive }) => (
            <>
              <Icon name={item.icon} size={20} strokeWidth={1.8} className="mx-auto" />
              <span className={cn('mt-[3px] block text-[9.5px]', isActive && 'font-bold')}>
                {item.label}
              </span>
              {typeof item.badge === 'number' ? (
                <span className="absolute right-3.5 top-1.5 rounded-pill bg-danger px-[5px] py-px text-[8.5px] font-bold text-white">
                  {item.badge}
                </span>
              ) : null}
            </>
          )}
        </NavLink>
      ))}
    </nav>
  );
}
